import { useCallback } from "react";
import { mutate } from "swr";
import wretch from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import { msalApp, msalConfig } from "../context/authContext";
import { FetcherOptions, PaginationQuery, PaginationQueryWithFields } from "../types";
import { authMiddleware } from "./middleware";

export const buildUrl = <T>(url: string, query: PaginationQuery | PaginationQueryWithFields<T>): string => {
  const params = new URLSearchParams();
  !!query.limit && params.set("limit", `${query.limit}`);
  !!query.page && params.set("page", `${query.page}`);
  !!query.q && params.set("q", query.q);
  !!query.orderBy && params.set("order_by", query.orderBy);
  query.desc != undefined && params.set("order", query.desc ? "desc" : "asc");
  query.filters?.length && params.set("filters", JSON.stringify(query.filters));

  if ("fields" in query) {
    query.fields?.length && params.set("fields", JSON.stringify(query.fields));
  }

  const op = url.includes("?") ? "&" : "?";

  return url + (params.size > 0 ? `${op}${params.toString()}` : "");
};

export const useApi = () => {
  // const auth = useAuth();

  const handleError = async (error: any) => {
    if (error && error.status === 401) {
      // Redirect to the logout + login page
      await msalApp.logoutRedirect({
        postLogoutRedirectUri: window.location.href,
      });
      await msalApp.loginRedirect();
    }
    throw error; // Re-throw error for further handling if needed
  };

  const w = wretch()
    .addon(QueryStringAddon)
    .middlewares([
      authMiddleware({
        scopes: [msalConfig.auth.clientId + "/.default"],
      }),
    ]);

  const isAbortError = (error: unknown): error is DOMException =>
    error instanceof DOMException && error.name === "AbortError";

  const api = w.url("/api");

  const get = useCallback(
    async <Response>({ url, signal, headers }: FetcherOptions): Promise<Response> => {
      try {
        return await api
          .options({ signal })
          .url(url)
          .headers(headers || {})
          .get()
          .json<Response>();
      } catch (error) {
        if (isAbortError(error)) {
          console.warn("Fetch request aborted:", url);
          return {} as Response;
        }
        throw error; // Rethrow other errors
      }
    },
    [api],
  );

  const post = useCallback(
    <Body, Response>(url: string, body?: Body) => api.url(url).post(body).json<Response>(),
    [api],
  );

  const postNoResult = useCallback(<Body>(url: string, body?: Body) => api.url(url).post(body).res(), [api]);

  const put = useCallback(<Body>(url: string, body: Body) => api.url(url).put(body).res(), [api]);

  const del = useCallback((url: string) => api.url(url).delete().res(), [api]);

  const mutateCache = (keys: string[], skipKey?: string) => {
    for (const k of keys) {
      mutate(
        (key) => {
          if (typeof key === "string" && key !== skipKey) {
            return key === k || key.toLowerCase().startsWith(`${k}?`);
          }
          return false;
        },
        undefined,
        {
          revalidate: true,
        },
      );
    }
  };

  const createObjectURL = useCallback(
    (url: string, redirectError?: boolean) =>
      w
        .options({ redirect: redirectError ? "error" : undefined })
        .url(url)
        .get()
        .blob()
        .then((blob) => URL.createObjectURL(blob))
        .catch(handleError),
    [w],
  );

  const downloadFile = useCallback(
    (url: string, fileName: string) =>
      createObjectURL(url, true)
        .then((res) => {
          const link = document.createElement("a");
          link.href = res;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch(handleError),
    [createObjectURL],
  );

  return {
    get,
    post,
    postNoResult,
    put,
    del,
    mutateCache,
    downloadFile,
    createObjectURL,
  };
};
